import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Star, Loader2, Send } from 'lucide-react';
import { submitProviderReview } from '@/services/providerService';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface ProviderReviewFormProps {
  providerId: string;
  providerUserId: string;
}

export const ProviderReviewForm = ({ providerId, providerUserId }: ProviderReviewFormProps) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');

  // Don't show review form if user is the provider themselves or not logged in
  if (!user || user.id === providerUserId) return null;

  const submitMutation = useMutation({
    mutationFn: () => submitProviderReview(providerId, rating, comment || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-reviews', providerId] });
      queryClient.invalidateQueries({ queryKey: ['provider', providerId] });
      toast.success(t('provider.reviews.submitSuccess', { defaultValue: 'Review submitted successfully!' }));
      setRating(0);
      setComment('');
    },
    onError: (error: any) => {
      const msg = error?.message || '';
      if (msg.includes('contact')) {
        toast.error(t('provider.reviews.mustContact', { defaultValue: 'You must contact the provider before leaving a review' }));
      } else {
        toast.error(t('provider.reviews.submitError', { defaultValue: 'Failed to submit review' }));
      }
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          {t('provider.reviews.leaveReview', { defaultValue: 'Leave a Review' })}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Star Rating */}
        <div>
          <p className="text-sm text-muted-foreground mb-2">
            {t('provider.reviews.yourRating', { defaultValue: 'Your rating' })}
          </p>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className="p-1 transition-transform hover:scale-110"
              >
                <Star
                  className={`w-7 h-7 ${
                    star <= (hoverRating || rating)
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-muted-foreground/30'
                  } transition-colors`}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Comment */}
        <Textarea
          placeholder={t('provider.reviews.commentPlaceholder', { defaultValue: 'Share your experience (optional)' })}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={3}
          maxLength={500}
        />

        <Button
          onClick={() => submitMutation.mutate()}
          disabled={rating === 0 || submitMutation.isPending}
          className="w-full"
        >
          {submitMutation.isPending ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('common.processing')}</>
          ) : (
            <><Send className="w-4 h-4 mr-2" />{t('provider.reviews.submit', { defaultValue: 'Submit Review' })}</>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};
